import React, { useState, useEffect, useContext } from "react";
import * as Sentry from "@sentry/react";
import { findFundsGroupInHierarchy, findIfSubFundInHierarchy } from "./utils";
import {
  TextField,
  Box,
  FormControl,
  Autocomplete,
  Switch,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Button from "../../../components/Button";
import { TitleText } from "../../component_styles";
import { LineText } from "../table_styles";
import UserContext from "../../../assets/user_context";
import {
  useFundNumberValidation,
  validateFundNumber,
  validateFundName,
  validateGroup,
  validateSubAccount,
} from "./validation";
import { useSubmitFund } from "./add_to_db_logic";
import { isExpectedFundError } from "./expectedErrors";
import LoadingLogo from "../../../assets/logos/animated-logo.svg?react";
import styled from "@emotion/styled";

const LoadingLogoStyled = styled(LoadingLogo)`
  z-index: 5;
  width: 50%;
`;

const AddFund = ({
  fundNumberToAdd,
  possibleFundNameToAdd,
  setShowModal,
  db,
  thisGroup,
  options,
  fundDataToEdit,
  clickedAdd,
  reenableSubmit,
  disabledAddButton,
}) => {
  const { org, funds, accounts, accountsHierarchy, fundsHierarchy } =
    useContext(UserContext);
  const [fundEntryData, setFundEntryData] = useState({
    fundName: possibleFundNameToAdd
      ? possibleFundNameToAdd
      : fundDataToEdit?.fundName
        ? fundDataToEdit.fundName
        : "",
    fundNumber: fundNumberToAdd
      ? fundNumberToAdd
      : fundDataToEdit?.fundNumber
        ? fundDataToEdit.fundNumber
        : "",
    isRegister: fundDataToEdit?.isRegister ? fundDataToEdit.isRegister : false,
    isPayableReceivable: fundDataToEdit?.isPayableReceivable
      ? fundDataToEdit.isPayableReceivable
      : false,
    group: thisGroup
      ? thisGroup
      : options?.group
        ? options.group
        : fundDataToEdit?.group
          ? fundDataToEdit.group
          : "",
    subFund: fundDataToEdit?.subFund ? fundDataToEdit.subFund : false,
    subFundOf: fundDataToEdit?.subFundOf ? fundDataToEdit.subFundOf : null,
    isRestricted: fundDataToEdit?.isRestricted
      ? fundDataToEdit.isRestricted
      : false,
    donorReason: fundDataToEdit?.donorReason
      ? fundDataToEdit.donorReason
      : "",
  });
  const matchOption = fundNumberToAdd && funds.length > 0;
  const [fundEntryErrors, setFundEntryErrors] = useState({
    fundName: false,
    fundNumber: false,
    group: false,
    subFund: false,
  });
  const [fundEntryFeedback, setFundEntryFeedback] = useState({
    fundName: "",
    fundNumber: "",
    group: "",
    subFund: "",
  });

  const [existingFundNumbers, setExistingFundNumbers] = useState(
    funds ? funds.map((fund) => fund.fundNumber) : [],
  );

  const editingFundId = fundDataToEdit?.fundDatabaseId
    ? fundDataToEdit.fundDatabaseId
    : null;

  useFundNumberValidation({
    number: fundEntryData.fundNumber,
    setFundEntryErrors,
    setFundEntryFeedback,
    existingFundNumbers,
    funds,
    fundDataToEdit,
    fundEntryData,
  });

  useEffect(() => {
    setExistingFundNumbers(funds ? funds.map((fund) => fund.fundNumber) : []);
  }, [funds]);

  //log out the fundEntryFeedback and fundEntryErrors when they change
  useEffect(() => {
    console.log("Fund Entry Feedback: ", fundEntryFeedback);
    console.log("Fund Entry Errors: ", fundEntryErrors);
    console.log("Fund entry data: ", fundEntryData, possibleFundNameToAdd);
  }, [fundEntryFeedback, fundEntryErrors]);

  const handleChange = (event) => {
    console.log(fundEntryData);
    const { name, value, type, checked } = event.target;
    const inputValue = type === "checkbox" ? checked : value;

    if (name === "fundNumber" && value !== "") {
      if (/^[1-9][0-9]*\.?[0-9]*$/.test(inputValue)) {
        setFundEntryData({
          ...fundEntryData,
          [name]: inputValue,
        });
      }
    } else {
      setFundEntryData({
        ...fundEntryData,
        [name]: inputValue,
      });
    }
  };

  //TODO: This is a dumb workaround to the fact that the account number is not set on initial load
  // useEffect(() => {
  //     if (accountDataToEdit) {
  //         setTimeout(() => {
  //             validateAccountNumber({
  //                 accountEntryData,
  //                 setAccountEntryErrors,
  //                 accountEntryErrors,
  //                 accountEntryFeedback,
  //                 setAccountEntryFeedback,
  //                 accountDataToEdit,
  //             });
  //         }, 10);
  //     }
  // }, []);

  //useEffect to validate subAccount if entryData changes and it is already an error
  useEffect(() => {
    if (fundEntryErrors.subFund) {
      validateSubAccount({
        accountEntryData: fundEntryData,
        setAccountEntryErrors: setFundEntryErrors,
        setAccountEntryFeedback: setFundEntryFeedback,
      });
    }
  }, [fundEntryData]);

  const { handleSubmit } = useSubmitFund({
    fundEntryData,
    editingFundId,
    setShowModal,
    setFundEntryData,
    fundDataToEdit,
    db,
  });

  const handleClick = async (event) => {
    event.preventDefault();
    if (clickedAdd) clickedAdd();
    console.log(fundEntryData);
    // Validate group (same validation works as account)
    validateFundNumber({
      fundEntryData,
      setFundEntryErrors,
      setFundEntryFeedback,
      fundDataToEdit,
    });
    validateFundName({
      fundEntryData,
      setFundEntryErrors,
      setFundEntryFeedback,
    });
    validateGroup({
      accountEntryData: fundEntryData,
      setAccountEntryErrors: setFundEntryErrors,
      setAccountEntryFeedback: setFundEntryFeedback,
    });

    // Validate sub-fund
    validateSubAccount({
      accountEntryData: fundEntryData,
      setAccountEntryErrors: setFundEntryErrors,
      setAccountEntryFeedback: setFundEntryFeedback,
    });

    // Final validation before submission
    if (
      (!fundEntryData.subFund || fundEntryData.subFundOf) &&
      fundEntryData.group !== "" &&
      fundEntryData.fundNumber !== "" &&
      fundEntryData.fundName !== ""
    ) {
      // Await the submit so a thrown save error is caught and surfaced via the
      // form's feedback mechanism instead of becoming an unhandled rejection
      // that leaves the button stuck disabled.
      try {
        await handleSubmit();
      } catch (error) {
        console.error("Error submitting fund:", error);
        setFundEntryErrors((prev) => ({ ...prev, fundName: true }));
        setFundEntryFeedback((prev) => ({
          ...prev,
          fundName: error?.message || "Failed to save the fund. Please try again.",
        }));
        reenableSubmit();
        // Validation throws are user-fixable (field feedback above). Report
        // only unexpected failures to Sentry (mirrors InlineFundForm).
        if (!isExpectedFundError(error)) {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "coa-fund-save");
            scope.setTag("operation", editingFundId ? "update" : "create");
            scope.setContext("fundSaveContext", {
              orgId: org,
              fundId: editingFundId,
            });
            Sentry.captureException(error);
          });
        }
      }
    } else {
      reenableSubmit();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}>
      {editingFundId ? (
        <TitleText>Edit Fund Details</TitleText>
      ) : !matchOption ? (
        <TitleText>Add New Fund</TitleText>
      ) : null}

      {!disabledAddButton ? (
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            pb: 3,
            pl: 2,
            pr: 2,
          }}
          noValidate
          autoComplete="off">
          {matchOption && <h2>Create New Fund</h2>}
          <FormControl>
            <TextField
              sx={{
                width: "350px",
                marginRight: "0px",
                fontSize: "22px",
                marginBottom: "20px",
              }}
              label="Fund Name"
              name="fundName"
              variant="outlined"
              color="secondary"
              value={fundEntryData.fundName}
              onChange={handleChange}
              error={fundEntryErrors.fundName}
              helperText={fundEntryFeedback.fundName}
              disabled={possibleFundNameToAdd ? true : false}
              required
              onBlur={() => {
                validateFundName({
                  fundEntryData,
                  setFundEntryErrors,
                  setFundEntryFeedback,
                });
              }}
            />
          </FormControl>
          <FormControl>
            <TextField
              sx={{
                width: "350px",
                fontSize: "22px",
                marginRight: "0px",
                marginLeft: "0px",
              }}
              label="Fund Number"
              name="fundNumber"
              variant="outlined"
              color="secondary"
              value={fundEntryData.fundNumber}
              onChange={handleChange}
              error={fundEntryErrors.fundNumber}
              helperText={fundEntryFeedback.fundNumber}
              required
              onBlur={() => {
                validateFundNumber({
                  fundEntryData,
                  setFundEntryErrors,
                  setFundEntryFeedback,
                  fundDataToEdit,
                });
              }}
              disabled={fundNumberToAdd ? true : false}
            />
          </FormControl>

          {funds?.length > 0 && (
            <React.Fragment>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}>
                <Switch
                  checked={fundEntryData.subFund}
                  color="secondary"
                  onChange={(event) => {
                    setFundEntryData({
                      ...fundEntryData,
                      subFund: event.target.checked,
                      subFundOf: null,
                    });
                  }}
                  name="subFund"
                  inputProps={{ "aria-label": "secondary checkbox" }}
                />
                <LineText>
                  This {fundEntryData.subFund ? "is" : "isn't"} a Sub Fund
                </LineText>
              </div>

              {fundEntryData.subFund && (
                <Autocomplete
                  key={`subFund-${fundEntryData.fundNumber}-selector`}
                  id="subFund"
                  name="subFund"
                  value={fundEntryData.subFundOf}
                  options={funds.filter(
                    (fund) =>
                      !findIfSubFundInHierarchy(fundsHierarchy, fund.id) &&
                      fund.id !== editingFundId,
                  )}
                  getOptionLabel={(option) => option.fundName}
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    console.log("New Value: ", newValue);
                    let foundFundGroup = null;
                    if (newValue !== null && newValue?.id) {
                      foundFundGroup = findFundsGroupInHierarchy(
                        fundsHierarchy,
                        newValue.id,
                      );
                    }

                    // Update the subFundOf state
                    setFundEntryData({
                      ...fundEntryData,
                      subFundOf: newValue,
                      group: foundFundGroup?.groupName || "",
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        width: "350px",
                        fontSize: "22px",
                      }}
                      label="Sub Fund of..."
                      name="subFund"
                      color="secondary"
                      variant="outlined"
                      error={fundEntryErrors.subFund}
                      helperText={fundEntryFeedback.subFund}
                      disabled={!fundEntryData.subFund}
                      required
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.fundName}
                    </li>
                  )}
                />
              )}
            </React.Fragment>
          )}

          <Autocomplete
            freeSolo
            id="group"
            name="group"
            value={fundEntryData.group}
            onInputChange={(event, newValue) => {
              setFundEntryData({
                ...fundEntryData,
                group: newValue,
              });
            }}
            options={(fundsHierarchy?.groups || []).map(
              (group) => group.groupName,
            )}
            disabled={fundEntryData.subFund || options?.group}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  width: "350px",
                  fontSize: "22px",
                }}
                label="Group"
                name="group"
                color="secondary"
                variant="outlined"
                onBlur={() => {
                  validateGroup({
                    accountEntryData: fundEntryData,
                    setAccountEntryErrors: setFundEntryErrors,
                    setAccountEntryFeedback: setFundEntryFeedback,
                  });
                }}
                error={fundEntryErrors.group}
                helperText={fundEntryFeedback.group}
                disabled={fundEntryData.subFund || options?.group}
                required
              />
            )}
          />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <FormControlLabel
              required
              variant="outlined"
              control={
                <Checkbox
                  color="secondary"
                  checked={fundEntryData.isRestricted}
                  name="isRestricted"
                  onChange={handleChange}
                />
              }
              label={"Donor Restricted Fund"}
            />
          </div>

          <FormControl>
            <TextField
              sx={{
                width: "350px",
                fontSize: "22px",
              }}
              label="Donor-Facing Name (Optional)"
              name="donorReason"
              variant="outlined"
              color="secondary"
              value={fundEntryData.donorReason}
              onChange={handleChange}
              placeholder={fundEntryData.fundName || "e.g., Building Fund"}
              helperText="If set, this name will be shown to donors instead of the fund name"
            />
          </FormControl>

          <Button
            data-testid="foundation-funds-legacy-button-submit"
            disabled={
              Object.values(fundEntryErrors).some((err) => err) ||
              disabledAddButton
            }
            variant="contained"
            onClick={handleClick}>
            {fundDataToEdit?.fundDatabaseId ? "Edit Fund" : "Add Fund"}
          </Button>
        </Box>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}>
          <LoadingLogoStyled />
          <LineText>Adding Fund...</LineText>
        </div>
      )}
    </div>
  );
};

export default AddFund;
